/* global dataLayer */
import { SubmissionError, submit, change } from 'redux-form';
import * as Sentry from '@sentry/browser';
import trans from 'counterpart';
import { normalize } from 'normalizr';
import debounce from 'lodash.debounce';
import * as common from '../../utilities/common';
import { manageModeSelector } from '../../selectors/environmentSelectors';
import { fetchSubjects, getSources, fetchAccommodationType, getCountries } from '../booking/actions';
import { get, post } from '../../utilities/api';
import { postReservationProposal, postReservationConfirmation } from '../../api/reservationApi';
import * as t from './actionTypes';
import { criteriaToOfferParams, reservationToParams, updateSubjects, paramsToCriteria } from './utilities';
import { objectSchema } from '../booking/schemas';
import { getBookingSectionBlocks, discountSelector } from './selectors';
import { setEntities } from '../../actions/entityActions';
import { getSections } from '../content/selectors';

export const setSection = ({ previous, current, next }) => ({
  type: t.SET_SECTION,
  payload: {
    previous,
    current,
    next
  }
});

export const setBookingSection = payload => (dispatch, getState) => {
  const state = getState();
  const bookingSections = getBookingSectionBlocks(state);
  let current = null;
  let previous = null;
  let next = null;
  switch (payload.sort) {
    case 0: {
      [current, next] = bookingSections;
      break;
    }
    case 1: {
      [previous, current, next] = bookingSections;
      break;
    }
    case 2: {
      [, previous, current] = bookingSections;
      break;
    }
    default: {
      break;
    }
  }
  return dispatch(setSection({
    previous,
    current,
    next,
  }));
};

export function setCustomer(customer) {
  return {
    type: t.SET_CUSTOMER,
    customer,
  };
}

export function setFilters(filters) {
  return {
    type: t.SET_FILTERS,
    filters,
  };
}

export function setOffer(offer) {
  return {
    type: t.SET_OFFER,
    offer,
  };
}

export function updatePropertyCounts(properties) {
  return {
    type: t.UPDATE_PROPERTY_COUNTS,
    properties,
  };
}

export function setAdditionCategory(category) {
  return {
    type: t.SET_ADDITION_CATEGORY,
    category,
  };
}

export function setObjects(objects) {
  return {
    type: t.SET_OBJECTS,
    payload: {
      objects,
    },
  };
}

export const customerAddressError = () => ({ type: t.CUSTOMER_ADDRESS_ERROR });
export const clearCustomerAddressError = () => ({ type: t.CLEAR_CUSTOMER_ADDRESS_ERROR });

/**
 * Fetch the address with the given postcode data
 *
 * @param {object} criteria
 */
const fetchAddress = debounce((criteria, dispatch, getState) => {
  const state = getState();

  if (!state.form || !state.form.customer || !state.form.customer.values) {
    return Promise.resolve();
  }

  const customer = state.form.customer.values;

  const params = {
    country: customer.country,
    postcode: customer.zipcode,
    streetnumber: customer.housenumber,
    ...criteria,
  };

  if (!params.country || !params.postcode || !params.streetnumber
    || params.country === '' || params.postcode === '' || params.streetnumber === '') {
    return Promise.resolve();
  }

  if (['NL', 'BE'].indexOf(params.country) === -1) {
    return Promise.resolve();
  }

  // Clear any address errors, since we're about to fetch again
  if (state.reservation.customer && state.reservation.customer.addressError) {
    dispatch(setCustomer({ addressError: false }));
  }

  const queryParams = {
    auth_key: 'JIJElVZXyWfLrjSa',
    streetnumber: params.streetnumber,
  };

  if (params.streetnumber_suffix) {
    queryParams.extension = params.streetnumber_suffix;
  }

  if (params.country === 'BE') {
    queryParams.be_fourpp = params.postcode;
  } else {
    queryParams.nl_sixpp = params.postcode;
  }

  return get({
    url: '/autocomplete',
    params: queryParams,
    baseURL: 'https://api.pro6pp.nl/v1'
  })
    // If successful, populate the address and city fields
    .then(({ data }) => {
      if (data.status === 'error') {
        throw new Error();
      }

      if (data.results.length > 0) {
        const result = data.results[0];

        dispatch(change('customer', 'address1', result.street));
        dispatch(change('customer', 'city', result.city));
      }
      return Promise.resolve();
    })
    // If no address was found,
    .catch(() => dispatch(setCustomer({ addressError: true })));
}, 300);

export const getAddress = criteria => (dispatch, getState) => fetchAddress(criteria, dispatch, getState);

export function getAvailability(criteria = null) {
  return (dispatch, getState) => {
    if (manageModeSelector(getState()) === 'manage') {
      return Promise.resolve();
    }

    if (!criteria) {
      criteria = getState().reservation.criteria; // eslint-disable-line prefer-destructuring
    }
    const params = criteriaToOfferParams(criteria);
    return get({
      url: '/offers',
      params
    })
      .then(({ data }) => {
        if (data.length > 0) {
          return dispatch(setOffer(data[0]));
        }
        // dispatch(setFilters({
        //   properties: json.properties,
        // }));
        return null;
      });
  };
}

export const getAvailableProperties = (criteria = null) => (dispatch, getState) => {
  if (!criteria) {
    criteria = getState().reservation.criteria; // eslint-disable-line prefer-destructuring
  }
  const params = criteriaToOfferParams(criteria);

  return get({ url: '/offers/properties', params })
    .then(({ data }) => dispatch(updatePropertyCounts(data.properties)));
};

export function reservationProposalFailure(errors) {
  return {
    type: t.RESERVATION_PROPOSAL_FAILURE,
    errors,
  };
}

export function reservationProposalSuccess(reservation, criteria) {
  return {
    type: t.RESERVATION_PROPOSAL_SUCCESS,
    reservation,
    criteria,
  };
}

export function setReservationCriteria(criteria) {
  return {
    type: t.SET_RESERVATION_CRITERIA,
    criteria,
  };
}

export function reservationConfirmationSuccess(reservation, criteria) {
  return {
    type: t.RESERVATION_CONFIRMATION_SUCCESS,
    reservation,
    criteria,
  };
}

export function reservationConfirmationFailure(errors) {
  return {
    type: t.RESERVATION_CONFIRMATION_FAILURE,
    errors,
  };
}

export function startReservationProposal() {
  return {
    type: t.RESERVATION_PROPOSAL_START,
  };
}

export function startReservationConfirmation() {
  return {
    type: t.RESERVATION_CONFIRMATION_START,
  };
}

// Create a customer from the customer form fields
export const createCustomer = (callback, errorCallback) => (dispatch, getState) => {
  const state = getState();
  if (!state.form.customer || state.form.customer.syncErrors) {
    // Dispatch the remote submit to trigger field errors
    dispatch(submit('customer'));
    dispatch(reservationConfirmationFailure({ customer: trans('action.create_customer_error') }));
    errorCallback();
    return Promise.resolve();
  }

  const customer = getState().form.customer.values;
  return post({ url: '/customers', data: customer })
    .then(({ data }) => {
      dispatch(setCustomer(data));

      return callback();
    })
    .catch((error) => {
      dispatch(reservationConfirmationFailure(error.errors));

      return errorCallback();
    });
};

export const createReservationProposal = (data = null) => (dispatch, getState) => {
  dispatch(startReservationProposal());
  if (manageModeSelector(getState()) === 'manage') {
    dispatch(reservationProposalSuccess({
      arrival_date: '2017-08-04T00:00:00+0200',
      departure_date: '2017-08-07T00:00:00+0200',
      reserved_resources: [
        {
          cancel_instead_of_remind: false,
          code: 'JUN',
          completed: false,
          end_date: '2017-08-07T0::0+0200',
          has_price_override: false,
          name: 'Jungalow',
          object_id: 2033,
          price: 715,
          quantity: 1,
          removable: false,
          resource_id: 4,
          start_date: '2017-08-04T00:00:00+0200',
          type: 'accommodationtype',
        },
        {
          cancel_instead_of_remind: false,
          code: 'RK',
          completed: false,
          end_date: '2017-08-07T00:00:00+0200',
          has_price_override: false,
          name: 'Reserveringskosten',
          price: 25,
          quantity: 1,
          removable: false,
          resource_id: 42,
          start_date: '2017-08-04T00:00:00+0200',
          type: 'extra',
        },
      ],
      resort: 'Vakantiepark Beekse Bergen',
      resort_id: 21,
      total_price: 803.2,
    }, {
      arrival_date: '2017-08-04',
      duration: 3,
      resource_id: 4,
      subjects: [{ quantity: '2', subject_id: '9' }],
    }));
    return Promise.resolve();
  }

  // If no data is provided, get it from the current state
  let criteria = data;
  if (criteria === null) {
    criteria = reservationToParams(getState().reservation.criteria);
  }

  if (criteria.additions) {
    criteria.additions = criteria.additions.filter(a => parseInt(a.quantity, 10) !== 0);
  }

  Object.keys(criteria).forEach((i) => {
    if (criteria[i] === null || criteria[i] === undefined) {
      delete criteria[i];
    }
  });

  criteria.language = trans('locale');

  return postReservationProposal(criteria)
    .then(({ data }) => {
      dispatch(reservationProposalSuccess(data, criteria));
      dispatch({ type: t.RESERVATION_INITIAL_FETCH, payload: true });
    })
    .catch((error) => {
      const errors = error.response ? error.response.data.errors : [trans('error.unknown_reservation_error')];
      dispatch(reservationProposalFailure(errors));
    });
};

// See https://opifer.atlassian.net/browse/LIBV16-2033 for more info.
const tarifExtraPersonCodes = ['extra8020', 'extra8018', 'extra2023', 'extra8034', 'extra8022', 'extra8023', 'extra', 'kep', 'extra8020groupon', 'tarifextraperson'];

// Above is disabled for now. Needs testing first
const getReservedResourcePrice = (resource) => {
  if (resource.type === 'accommodationtype' || resource.price < 0 || resource.code === 'extra') {
    return parseFloat(resource.price);
  }

  return 0;
};

const pushReservationToDataLayer = (reservation, discount) => {
  if (!dataLayer) {
    return Sentry.captureException(new Error('GA DataLayer is not available'));
  }

  let affiliateTotal = parseFloat(reservation.reserved_resources.find(r => r.type === 'accommodationtype').price);
  if (reservation.resort_id === 2041 || reservation.resort_id === 21) {
    const extraPersonsPrice = reservation.reserved_resources.find(r => r.code === 'extra');
    if (extraPersonsPrice) {
      affiliateTotal = affiliateTotal + parseFloat(extraPersonsPrice.price);
    }
  }

  let reservationNumber = (reservation.reservation_number) ? reservation.reservation_number.toString() : '';
  reservationNumber = (reservationNumber.endsWith('000')) ? reservationNumber.substring(0, reservationNumber.length - 3) : reservationNumber;
  const data = {
    ecommerce: {
      purchase: {
        actionField: {
          id: `${reservationNumber}`, // required -unique Transaction ID
          revenue: `${parseFloat(reservation.total_price)}`, // required -(incl. tax and shipping)
          tax: 0, // optional -using this as tourist tax?
          AffiliateTotal: `${affiliateTotal + parseFloat(discount)}`, // required–Transaction total without tax and shipping
          transactionId: `${reservationNumber}`, //  required -unique Transaction ID
          coupon: `${reservation.criteria.special_code == null ? 'no special code' : reservation.criteria.special_code}`, // optional -coupon used on total order
        },
        products: reservation.reserved_resources.map(resource => ({ // list of productFieldObjects.
          name: `${resource.name}`, // required -Descriptive name in Dutch or English
          id: `${resource.code}`, // required -Unique Product SKU
          price: `${getReservedResourcePrice(resource)}`, // required -Product Price
          category: `${resource.type}`, // optional -The category of the product
          quantity: parseInt(resource.quantity, 10), // required -Product quantity
          coupon: '', // optional -Coupon used on this product
        })),
      },
    },
    event: 'ecomPurchase',
  };

  dataLayer.push(data);
  return null;
};

export const confirmReservation = (data = null, onErrorCallback) => (dispatch, getState) => {
  dispatch(startReservationConfirmation());
  let criteria = data;
  if (criteria === null) {
    // If no data is provided, get it from the current state
    criteria = reservationToParams(getState().reservation.criteria);
  }

  if (criteria.additions) {
    criteria.additions = criteria.additions.filter(a => parseInt(a.quantity, 10) !== 0);
  }

  Object.keys(criteria).forEach((i) => {
    if (criteria[i] === null || criteria[i] === undefined) {
      delete criteria[i];
    }
  });

  criteria.language = trans('locale');

  return dispatch(createCustomer(() => {
    criteria.customer_id = getState().reservation.customer.customer_id;
    criteria.remark = getState().form.customer.values.comments;
    return postReservationConfirmation(criteria)
      .then((reservationResponse) => {
        dispatch(reservationConfirmationSuccess(reservationResponse.data, criteria));
        // we get the getState because reservationResponse does not include the criteria
        pushReservationToDataLayer(getState().reservation, discountSelector(getState()));
        return reservationResponse.data;
      })
      .catch((error) => {
        const errors = error.response ? error.response.data.errors : {};
        dispatch(reservationConfirmationFailure(errors));
        if (onErrorCallback) {
          return onErrorCallback();
        }
        return false;
      });
  }, () => {
    console.log('CREATE CUSTOMER ERROR');
    if (onErrorCallback) {
      onErrorCallback();
    }
  }));
};

export function setInitialSections(current, next) {
  return {
    type: t.SET_INITIAL_SECTIONS,
    current,
    next,
  };
}

export const initializeSections = () => (dispatch, getState) => {
  const bookingSections = common.objectToArray(getState().entities.blocks)
    .filter(b => b.type === 'BookingSection').sort((a, b) => a.sort - b.sort);

  dispatch(setInitialSections(bookingSections[0], bookingSections[1]));
};

export function setCurrentSection(sections, current) {
  return {
    type: t.SET_CURRENT_SECTION,
    sections,
    current,
  };
}

export function setNextSection(sections) {
  return {
    type: t.SET_NEXT_SECTION,
    sections,
  };
}

export const nextSection = sections => (dispatch, getState) => {
  if (sections[sections.length - 1].id === getState().reservation.sections.current.id) {
    dispatch(confirmReservation());
  } else {
    dispatch(setNextSection(sections));
  }
};

export function previousSection(sections) {
  return {
    type: t.PREVIOUS_SECTION,
    sections,
  };
}

export function addAddition(additionId, quantity) {
  return {
    type: t.ADD_ADDITION,
    additionId,
    quantity,
  };
}

export function removeAddition(additionId) {
  return {
    type: t.ADD_ADDITION,
    additionId,
  };
}

export const initializeReservation = params => (dispatch, getState) => {
  const state = getState();
  // Clear the reservation, since we might have received a reservation from the local storage
  dispatch({ type: t.CLEAR_RESERVATION });
  dispatch({ type: t.RESERVATION_INITIAL_FETCH, payload: false });
  dispatch(fetchAccommodationType(params.resource));
  dispatch(fetchSubjects());

  const bookingSections = common.objectToArray(state.entities.blocks)
    .filter(b => b.type === 'BookingSection').sort((a, b) => a.sort - b.sort);
  if (bookingSections.length) {
    dispatch(setInitialSections(bookingSections[0], bookingSections[1]));
  }

  const criteria = paramsToCriteria(params);

  if (state.preferences.properties) {
    criteria.preferences = state.preferences.properties
      .map(property => ({ type: 'property', id: property.toString() }));
  }

  dispatch(setReservationCriteria(criteria));

  dispatch(createReservationProposal(null, true));
  dispatch(getAvailableProperties(criteria));
  dispatch(getAvailability(criteria));

  dispatch(getCountries());
  dispatch(getSources());
};

export const handleSubjectChange = values => (dispatch, getState) => {
  const criteria = getState().reservation.criteria; // eslint-disable-line prefer-destructuring
  criteria.subjects = updateSubjects(values, criteria.subjects);

  dispatch(setReservationCriteria(criteria));
  dispatch(createReservationProposal(criteria));
};

export function submitSubjects(values, dispatch, error, clearSubmitErrors) {
  if (parseInt(values['subject-9'], 10) === 0 && parseInt(values['subject-10'], 10) === 0) {
    throw new SubmissionError({
      'subject-9': trans('action.submit_subjects_error'),
      'subject-10': trans('action.submit_subjects_error'),
      _error: trans('action.submit_subjects_error'),
    });
  }
  if (error) {
    clearSubmitErrors();
  }
  return dispatch(handleSubjectChange(values));
}

export const getAvailableObjects = (reservationCriteria = null, resource) => (dispatch, getState) => {

  if (!reservationCriteria) {
    reservationCriteria = getState().reservation.criteria;
  }
  const criteria = {
    locale: trans('locale'),
    arrival: reservationCriteria.arrival_date,
    nights: reservationCriteria.duration,
    resource
  };

  if (reservationCriteria.special_code) {
    criteria.special = reservationCriteria.special_code;
  }

  const properties = reservationCriteria.preferences.filter(p => p.type === 'property');
  if (properties.length) {
    criteria.properties = properties.map(p => p.id);
  }

  return get({ url: '/reservation/objects', params: criteria })
    .then(({ data }) => normalize(data, [objectSchema]))
    .then((normalized) => {
      dispatch(setEntities(normalized.entities));
      return dispatch(setObjects(normalized.result));
    });
};

export const handleReservationReceiptSubmit = () => (dispatch, getState) => dispatch(confirmReservation(
  null,
  () => new Promise((resolve, reject) => {
    // If there were errors: open personal information section.
    const sections = getSections(getState());
    const current = sections.find(section => section.name === 'personal_details_section');
    dispatch(setCurrentSection(sections, current));
    return reject();
  })
));
